import { useCallback, useEffect, useState } from 'react'
import { StatusBadge, colors, btnPrimary, btnSecondary } from '../../components/ui'
import { api, ApiError } from '../../services/api'

interface SiteRow {
  readonly id: string
  readonly name: string
  readonly tenancyId: string | null
  readonly hasKey: boolean
  readonly isArchived: boolean
}
interface MintResponse {
  readonly id: string
  readonly name: string
  readonly plaintextKey: string
  readonly displayedOnce: true
}

export function CustomerSitesCard({ orgId }: { readonly orgId: string }) {
  const [rows, setRows] = useState<readonly SiteRow[]>([])
  const [name, setName] = useState('')
  const [mintedKey, setMintedKey] = useState<{ name: string; key: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await api.get<readonly SiteRow[]>(
        `/fleet/customers/${orgId}/sites` + (showArchived ? '?archived=true' : ''),
      )
      setRows(res.data ?? [])
    } catch {
      setError('Failed to load sites')
    }
  }, [orgId, showArchived])
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  async function addSite() {
    if (!name.trim()) return
    setError(null)
    try {
      const res = await api.post<MintResponse>(`/fleet/customers/${orgId}/sites`, { name: name.trim() })
      if (res.data) setMintedKey({ name: res.data.name, key: res.data.plaintextKey })
      setName('')
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Create failed')
    }
  }

  async function rotate(id: string, siteName: string) {
    if (!confirm(`Rotate the key for "${siteName}"? The old key stops working immediately.`)) return
    setError(null)
    try {
      const res = await api.post<MintResponse>(`/fleet/sites/${id}/rotate-key`, {})
      if (res.data) setMintedKey({ name: siteName, key: res.data.plaintextKey })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Rotate failed')
    }
  }

  async function archive(id: string, siteName: string) {
    if (
      !confirm(
        `Archive site "${siteName}"? Its instrument key stops authenticating immediately and it drops out of the active list.`,
      )
    )
      return
    setError(null)
    try {
      await api.post(`/fleet/sites/${id}/archive`, {})
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Archive failed')
    }
  }

  async function unarchive(id: string) {
    setError(null)
    try {
      await api.post(`/fleet/sites/${id}/unarchive`, {})
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unarchive failed')
    }
  }

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: '0.5rem',
        padding: '1rem',
        marginTop: '1rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h2 style={{ margin: 0, color: colors.textHeading }}>Sites (instrument keys)</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '13px' }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived
        </label>
      </div>
      {error && <p style={{ color: colors.danger }}>{error}</p>}
      {mintedKey && (
        <div
          data-testid="site-minted-key"
          style={{
            background: colors.bgSuccess,
            border: `1px solid ${colors.borderSuccess}`,
            borderRadius: '0.5rem',
            padding: '0.75rem',
            marginBottom: '0.75rem',
          }}
        >
          <strong>Site key for {mintedKey.name}.</strong> Shown exactly once -- paste it into that site&apos;s UDM
          (SiteKey). Lost it? Rotate the key.
          <pre style={{ userSelect: 'all', margin: '0.5rem 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {mintedKey.key}
          </pre>
          <button type="button" onClick={() => setMintedKey(null)} style={btnSecondary}>
            Done
          </button>
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New site name" style={{ flex: 1 }} />
        <button type="button" onClick={() => void addSite()} style={btnPrimary}>
          Add site
        </button>
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((s) => (
          <li
            key={s.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.4rem 0',
              borderTop: `1px solid ${colors.border}`,
            }}
          >
            <span>
              {s.name} <StatusBadge status={s.hasKey ? 'active' : 'inactive'} label={s.hasKey ? 'keyed' : 'no key'} />{' '}
              {s.tenancyId ? <StatusBadge status="active" label="tenant-linked" /> : null}{' '}
              {s.isArchived ? <StatusBadge status="inactive" label="archived" /> : null}
            </span>
            <span style={{ display: 'flex', gap: '0.5rem' }}>
              {!s.isArchived && (
                <button type="button" onClick={() => void rotate(s.id, s.name)} style={btnSecondary}>
                  Rotate key
                </button>
              )}
              {s.isArchived ? (
                <button type="button" onClick={() => void unarchive(s.id)} style={btnSecondary}>
                  Unarchive
                </button>
              ) : (
                <button type="button" onClick={() => void archive(s.id, s.name)} style={btnSecondary}>
                  Archive
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
